import React, { useEffect, useState } from 'react';
import { UserRound } from 'lucide-react';
import {
  CHAT_PLAYER_SEARCH_DEBOUNCE_MS,
  normalizeChatSearch,
  searchChatPlayerDirectory,
} from './chatPlayerDirectory';

export function useChatPlayerSearch({ search, tenantId, agentPortal, enabled }) {
  const term = normalizeChatSearch(search);
  const key = enabled && tenantId && term.length >= 2
    ? JSON.stringify([term, tenantId, Boolean(agentPortal)])
    : '';
  const [state, setState] = useState({ key: '', players: [], loading: false, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setState({ key, players: [], loading: true, error: '' });
    const timer = window.setTimeout(async () => {
      try {
        const players = await searchChatPlayerDirectory({ term, tenantId, agentPortal, signal: controller.signal });
        if (!cancelled) setState({ key, players, loading: false, error: '' });
      } catch {
        if (!cancelled) setState({ key, players: [], loading: false, error: 'Player search is unavailable. Try again.' });
      }
    }, CHAT_PLAYER_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [key, term, tenantId, agentPortal, attempt]);

  return {
    players: key && state.key === key ? state.players : [],
    loading: Boolean(key && (state.key !== key || state.loading)),
    error: key && state.key === key ? state.error : '',
    retry: () => setAttempt((value) => value + 1),
  };
}

export default function ChatPlayerSearch({ players, loading, error, retry, onOpen, openingPlayerId }) {
  if (loading) return <p className="p-3 text-sm text-slate-300" role="status">Searching players…</p>;
  if (error) return (
    <div className="p-3 text-sm text-slate-200" role="alert">
      <p>{error}</p>
      <button type="button" onClick={retry} className="mt-2 rounded-md px-3 py-2 text-sm text-[#FDE68A] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FDE68A]">Retry player search</button>
    </div>
  );
  if (!players.length) return null;
  return (
    <section aria-label="Matching players" data-testid="chat-player-search-results">
      <h3 className="px-3 pt-3 text-sm font-semibold text-slate-300">Players</h3>
      {players.map((player) => (
        <button
          key={player.id}
          type="button"
          onClick={() => onOpen(player)}
          disabled={Boolean(openingPlayerId)}
          data-testid={`chat-search-player-${player.id}`}
          className="flex w-full items-center gap-3 p-3 text-left text-white hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#FDE68A] disabled:opacity-60"
        >
          <UserRound className="h-5 w-5 shrink-0 text-slate-300" aria-hidden="true" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold">{player.full_name || player.username || 'Player'}</span>
            {player.username && <span className="block truncate text-sm text-slate-300">@{player.username}</span>}
            <span className="block text-sm text-slate-300">{openingPlayerId === player.id ? 'Opening chat…' : 'Open chat'}</span>
          </span>
        </button>
      ))}
    </section>
  );
}
